import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import { HF_BODY_STYLES } from '@root/app/shared/theme/layoutConstants';
import { HF_FONT_SIZES, HF_FONT_WEIGHTS, HF_SPACING } from '@root/app/shared/theme/designTokens';

const TERMS_URLS: Record<string, string> = {
    en: 'https://myhappyforce.com/en/terms-conditions/',
    es: 'https://myhappyforce.com/es/terminos-y-condiciones/'
};

const PRIVACY_URLS: Record<string, string> = {
    en: 'https://myhappyforce.com/en/privacy-policy/',
    es: 'https://myhappyforce.com/es/politica-de-privacidad/'
};

const resolveLangKey = (language: string): string => (
    language.startsWith('es') ? 'es' : 'en'
);

const linkSx = (theme: { palette: { primary: { main: string } } }) => ({
    display: 'inline',
    color: theme.palette.primary.main,
    fontSize: `${HF_FONT_SIZES.bodySm}px`,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' }
});

const HfFooter: React.FC = () => {
    const { t, getCurrentLanguage } = useTranslation();
    const year = new Date().getFullYear();
    const langKey = resolveLangKey(getCurrentLanguage());
    const termsUrl = TERMS_URLS[langKey] ?? TERMS_URLS.en;
    const privacyUrl = PRIVACY_URLS[langKey] ?? PRIVACY_URLS.en;

    return (
        <Box
            component='footer'
            className='footer warm-grey'
            sx={(theme) => ({
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.hfLayout.border}`,
                bgcolor: theme.palette.hfLayout.paper,
                color: theme.palette.text.secondary,
                px: `${HF_SPACING.lgPlus}px`,
                py: `${HF_SPACING.smPlus}px`,
                fontSize: `${HF_BODY_STYLES.fontSizePx}px`,
                lineHeight: '18.57px'
            })}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                    columnGap: 2
                }}
            >
                <Box
                    component='img'
                    src='/images/footer_hf.png'
                    alt='happyforce'
                    sx={(theme) => ({
                        justifySelf: 'start',
                        height: theme.hfLayout.sizes.footerLogoHeight,
                        display: 'block'
                    })}
                />
                <Box sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <Box
                        component='a'
                        href={termsUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={linkSx}
                    >
                        {t('TERMS_AND_CONDITIONS')}
                    </Box>
                    {' / '}
                    <Box
                        component='a'
                        href={privacyUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={linkSx}
                    >
                        {t('PRIVACY_POLICY')}
                    </Box>
                </Box>
                <Box
                    component='strong'
                    sx={{
                        justifySelf: 'end',
                        fontWeight: HF_FONT_WEIGHTS.bold,
                        fontSize: `${HF_FONT_SIZES.bodySm}px`,
                        color: 'inherit',
                        whiteSpace: 'nowrap'
                    }}
                >
                    &copy; Happyforce {year}.
                </Box>
            </Box>
        </Box>
    );
};

export default HfFooter;
